import ELK, { ElkNode, ElkExtendedEdge } from "elkjs/lib/elk.bundled.js";
import { Node, Edge } from "reactflow";

const elk = new ELK();

/** Default node dimensions for ELK (used before React Flow measures the DOM) */
const DEFAULT_NODE_WIDTH = 280;
const DEFAULT_NODE_HEIGHT = 225;

/** Spacing configuration */
const LAYER_SPACING = 120; // horizontal gap between columns (ranks)
const NODE_SPACING = 20;   // vertical gap between nodes in the same colum

interface LayoutOptions {
    direction?: "RIGHT" | "LEFT" | "DOWN" | "UP";
    /** Top-left origin for the layout. ELK positions are offset by this. */
    origin?: { x: number; y: number };
}

/**
 * Runs ELK mrtree layout on the given React Flow nodes and edges.
 *
 * - Layout is driven entirely by edge connections.
 *
 * - Sticky notes (ids starting with "S") are excluded from the layout graph.
 *   After layout they are repositioned 60px above their parent node (if
 *   `data.parentNodeId` is set), or left at their original position.
 *
 * - Any node with more than 1 incoming edge is vertically centered across
 *   all its source nodes after ELK computes positions (post-processing step).
 *
 * - All computed positions are offset by `options.origin`, anchored to N1.
 *
 * - Returns a new array of nodes with updated positions and the original edges
 *   (edges are untouched — ELK only computes node positions).
 */
export async function getLayoutedElements(
    nodes: Node[],
    edges: Edge[],
    options: LayoutOptions = {}
): Promise<{ nodes: Node[]; edges: Edge[] }> {
    const direction = options.direction ?? "RIGHT";
    const origin = options.origin ?? { x: 0, y: 0 };

    // ── Step 1: Separate sticky notes from layoutable nodes ─────────────────
    const stickyNotes = nodes.filter((n) => n.id.startsWith("S"));
    const layoutableNodes = nodes.filter((n) => !n.id.startsWith("S"));

    // ── Step 2: Build ELK children ──────────────────────────────────────────
    const elkChildren: ElkNode[] = layoutableNodes.map((node) => ({
        id: node.id,
        width: node.width ?? DEFAULT_NODE_WIDTH,
        height: node.height ?? DEFAULT_NODE_HEIGHT,
    }));

    // Only include edges whose source AND target are in the layoutable set
    const layoutableIds = new Set(layoutableNodes.map((n) => n.id));
    const elkEdges: ElkExtendedEdge[] = edges
        .filter((e) => layoutableIds.has(e.source) && layoutableIds.has(e.target))
        .map((e) => ({
            id: e.id,
            sources: [e.source],
            targets: [e.target],
        }));

    // ── Step 3: Run ELK layout ───────────────────────────────────────────────
    const elkGraph: ElkNode = {
        id: "root",
        layoutOptions: {
            "elk.algorithm": "mrtree",
            "elk.direction": direction,
            "elk.spacing.nodeNode": String(LAYER_SPACING), // mrtree uses this for spacing between layers
            "elk.spacing.nodeNodeBetweenLayers": String(LAYER_SPACING), // fallback
            "elk.spacing.componentComponent": String(NODE_SPACING), // spacing between nodes
            "elk.edgeRouting": "ORTHOGONAL",
        },
        children: elkChildren,
        edges: elkEdges,
    };

    const layoutResult = await elk.layout(elkGraph);

    // ── Step 4: Compute origin offset anchored to N1 ─────────────────────────
    // mrtree centers the root node vertically among its children, so N1's ELK
    // y-coordinate is not 0. We shift all positions so N1 lands at options.origin.
    const n1ElkNode = layoutResult.children?.find((c) => c.id === "N1");
    const offsetX = origin.x - (n1ElkNode?.x ?? 0);
    const offsetY = origin.y - (n1ElkNode?.y ?? 0);

    // ── Step 5: Build positionMap with offset applied ────────────────────────
    const positionMap = new Map<string, { x: number; y: number }>();
    for (const child of layoutResult.children ?? []) {
        positionMap.set(child.id, {
            x: (child.x ?? 0) + offsetX,
            y: (child.y ?? 0) + offsetY,
        });
    }

    // ── Step 6: Center any node with more than 1 incoming edge ───────────────
    // Counts incoming edges per node
    const incomingCountMap = new Map<string, number>();
    edges.forEach((e) => {
        incomingCountMap.set(e.target, (incomingCountMap.get(e.target) ?? 0) + 1);
    });

    // Collect nodes that need centering (2+ incoming edges)
    const nodesToCenter = new Set(
        [...incomingCountMap.entries()]
            .filter(([, count]) => count > 1)
            .map(([id]) => id)
    );

    for (const nodeId of nodesToCenter) {
        if (!layoutableIds.has(nodeId)) continue; // skip sticky notes

        const incomingEdges = edges.filter((e) => e.target === nodeId);

        const sourcePositions = incomingEdges
            .map((e) => positionMap.get(e.source))
            .filter(Boolean) as { x: number; y: number }[];

        if (sourcePositions.length === 0) continue;

        const nodeHeight =
            layoutableNodes.find((n) => n.id === nodeId)?.height ?? DEFAULT_NODE_HEIGHT;

        const minY = Math.min(...sourcePositions.map((p) => p.y));
        const maxY = Math.max(...sourcePositions.map((p) => p.y));

        // Center of source span, adjusted for target node's own height
        const centeredY = (minY + maxY) / 2 - nodeHeight / 2 + DEFAULT_NODE_HEIGHT / 2;

        const currentPos = positionMap.get(nodeId);
        if (currentPos) {
            positionMap.set(nodeId, {
                x: currentPos.x, // X untouched — ELK handles column placement
                y: centeredY,
            });
        }
    }

    // ── Step 7: Apply positions to layoutable nodes ──────────────────────────
    const layoutedNodes: Node[] = layoutableNodes.map((node) => {
        const pos = positionMap.get(node.id);
        return {
            ...node,
            position: pos ?? node.position,
        };
    });

    // ── Step 8: Reposition sticky notes relative to their parent ─────────────
    const repositionedStickyNotes: Node[] = stickyNotes.map((note) => {
        const parentId = note.data?.parentNodeId;
        if (parentId) {
            const parentPos = positionMap.get(parentId);
            if (parentPos) {
                return {
                    ...note,
                    position: {
                        x: parentPos.x,
                        y: parentPos.y - 60,
                    },
                };
            }
        }
        // No parent or parent not in layout — keep original position
        return note;
    });

    return {
        nodes: [...layoutedNodes, ...repositionedStickyNotes],
        edges,
    };
}

